import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState, AppDispatch } from '../store';
import { logout } from '../slices/authSlice';
import { API_BASE_URL } from '../../config/environment';
import { redirectToLogin } from '../../shared/utils/authRedirect';

export const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const baseQueryWithAuthRaw = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    
    if (token) {
      try {
        // Validate token before adding to headers
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp > currentTime) {
          headers.set('authorization', `Bearer ${token}`);
        } else {
          console.warn('Token expired, not adding to request headers');
        }
      } catch (error) {
        console.error('Invalid token format:', error);
      }
    }
    
    headers.set('accept', '*/*');
    
    return headers;
  },
});

export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Check token validity before making request
  const state = api.getState() as RootState;
  const token = state.auth.token;
  
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp < currentTime) {
        // Token expired, logout before making request
        const user = state.auth.user;
        api.dispatch(logout());
        
        redirectToLogin(user?.userType as 'student' | 'tutor' | null);
        
        return {
          error: {
            status: 401,
            statusText: 'Token expired',
            data: { message: 'Your session has expired. Please login again.' }
          } as FetchBaseQueryError
        };
      }
    } catch (error) {
      console.error('Invalid token format:', error);
    }
  }
  
  const result = await baseQueryWithAuthRaw(args, api, extraOptions);
  
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;
    const hasToken = !!state.auth.token;
    
    if (hasToken) {
      const user = state.auth.user;
      api.dispatch(logout());
      
      redirectToLogin(user?.userType as 'student' | 'tutor' | null);
    }
  }
  
  return result;
};

// Base query that adds auth if available but doesn't redirect on 401
export const baseQueryWithOptionalAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQueryWithAuthRaw(args, api, extraOptions);
  
  // Don't redirect on 401, just return the error
  // This allows endpoints to work for both authenticated and unauthenticated users
  return result;
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

export const checkTokenExpiration = (token: string | null, dispatch: AppDispatch): boolean => {
  if (!token) {
    return false;
  }
  
  if (isTokenExpired(token)) {
    dispatch(logout());
    redirectToLogin(null);
    return false;
  }
  
  return true;
};
